import { Method } from 'axios';
import { BehaviorSubject, Observable, Observer } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  ApiEndpointConfiguration,
  ConnectionStateService,
  IApiEndpointInformation,
  IAuthTokenService,
  IMobizHttpService,
  IServerApiBuilderService,
  MobizHttpErrorResponse,
  MobizServerApi,
  SerializableObject,
  UnknownNetworkError,
} from '../../core';
import { Inject, Injectable } from '../../injection';
import { NOTHING_TO_DO } from '../../rxjs-utils';

import { ConnectionHelperService } from './connection-helper.service';
import { TokenRefreshService } from './token-refresh.service';
import { AuthGrantType } from '../../core/services/auth-token-grant';

@Injectable()
export class MobizHttpAxiosService implements IMobizHttpService {
  public get networkConnectionStatus(): BehaviorSubject<boolean> {
    throw new Error('IMobizHttpService.networkConnectionStatus has been replaced by ConnectionStateService.STATE$.');
  }

  constructor(
    @Inject(IAuthTokenService) private readonly authTokenService: IAuthTokenService,
    @Inject(IServerApiBuilderService) private readonly serverApiBuilderService: IServerApiBuilderService,
    private readonly connectionHelperService: ConnectionHelperService,
    private readonly connectionStateService: ConnectionStateService,
    private readonly tokenRefreshService: TokenRefreshService,
  ) {
  }

  public checkNetworkConnectionStatus(_updateSubject?: boolean): Observable<boolean> {
    return this.connectionStateService.STATE$.refresh().pipe(
      map((): boolean => {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        return (<any>this.connectionStateService.STATE$).value?.api === 'online';
      }),
    );
  }

  public delete<T extends SerializableObject>(instance: T, config: ApiEndpointConfiguration<string>): Observable<T> {
    return this.dispatchRequest<T>('DELETE', config, instance);
  }

  public get<T extends SerializableObject>(instance: T, config: ApiEndpointConfiguration<string>): Observable<T> {
    return this.dispatchRequest<T>('GET', config, instance);
  }

  public post<T extends SerializableObject>(instance: T, config: ApiEndpointConfiguration<string>): Observable<T> {
    return this.dispatchRequest<T>('POST', config, instance);
  }

  public put<T extends SerializableObject>(instance: T, config: ApiEndpointConfiguration<string>): Observable<T> {
    return this.dispatchRequest<T>('PUT', config, instance);
  }

  public getDefaultHeadersOptions(): Record<string, string> {
    const defaultHeaders: Record<string, string> = {};
    this.getIdentificationHeaders().forEach((value: string, key: string): void => {
      defaultHeaders[key] = value;
    });
    defaultHeaders['Accept'] = 'application/json';
    defaultHeaders['Content-Type'] = 'application/json';
    return defaultHeaders;
  }

  public getIdentificationHeaders(information?: Required<IApiEndpointInformation>): Headers {
    const grantType: AuthGrantType = (information || this.serverApiBuilderService.DEFAULT_API_ENDPOINT_INFORMATION).grantType;
    return this.authTokenService.getIdentificationHeaders(grantType);
  }

  public refreshToken(): Observable<void> {
    return this.tokenRefreshService.refreshToken();
  }

  public refreshTokenInBackground(): void {
    this.refreshToken().subscribe(NOTHING_TO_DO);
  }

  private dispatchRequest<T extends SerializableObject>(
    requestMethod: Method,
    requestConfig: ApiEndpointConfiguration<string>,
    responseTypeInstance: T,
  ): Observable<T> {
    
    if (!requestConfig.headers || !('Accept-Language' in requestConfig.headers)) {
      requestConfig = {
        ...requestConfig,
        headers: {
          ...(requestConfig.headers || {}),
          'Accept-Language': document.documentElement.lang || 'is'
        }
      };
    }
    
    return new Observable((observer: Observer<T>): void => {
      const onRequestSuccess = (responseData: T): void => {
        observer.next(responseData);
        observer.complete();
      };

      const onRequestError = (error: MobizHttpErrorResponse | unknown): void => {
        observer.error(error);
      };

      const reRunRequest = (): void => {
        this.connectionHelperService.createRequestObservable(
          requestMethod,
          requestConfig,
          responseTypeInstance,
        ).subscribe(onRequestSuccess, onRequestError);
      };

      const onFirstRequestError = (error: MobizHttpErrorResponse | unknown): void => {
        if (this.requestRetryNeeded(requestConfig, error)) {
          this.connectionStateService.setNetworkConnectionOffline();
          this.connectionStateService.waitForNetworkConnection()
            .subscribe(reRunRequest, onRequestError);
        } else if (this.tokenRefreshNeeded(requestConfig, error)) {
          this.tokenRefreshService.refreshToken()
            .subscribe(reRunRequest, onRequestError);
        } else {
          onRequestError(error);
        }
      };

      const runFirstRequest = (): void => {
        this.connectionHelperService.createRequestObservable(
          requestMethod,
          requestConfig,
          responseTypeInstance,
        ).subscribe(onRequestSuccess, onFirstRequestError);
      };

      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      if ((<any>this.connectionStateService.STATE$).value?.api === 'online') {
        runFirstRequest();
      } else {
        this.connectionStateService.waitForNetworkConnection()
          .subscribe(runFirstRequest);
      }
    });
  }

  private requestRetryNeeded(
    requestConfig: ApiEndpointConfiguration<string>,
    error: MobizHttpErrorResponse | unknown,
  ): boolean {
    // Allow users to logout while offline.
    if (requestConfig.serverApi === MobizServerApi.AccountLogOut) {
      return false;
    }
    return error instanceof UnknownNetworkError;
  }

  private tokenRefreshNeeded(
    requestConfig: ApiEndpointConfiguration<string>,
    error: MobizHttpErrorResponse | unknown,
  ): boolean {
    // Ignore OAuth errors during logout.
    if (requestConfig.serverApi === MobizServerApi.AccountLogOut) {
      return false;
    }
    return error instanceof MobizHttpErrorResponse && error.isOAuthUnauthorizedError();
  }
}
